/* eslint-disable */
import markerIcon from '../../data/markerIcon';
import { DEFAULT, MAX_MARKER_ZOOM, ICON_TYPES } from '../../constants';

class MarkerUtil {
  /**
   * Clears the provided markers from the map.
   *
   * @param {Array<Object>} markers - Marker items to remove from the map.
   */
  clearMarkers(markers) {
    markers.forEach(({ marker }) => {
        marker.setMap(null);
     });
  }

  /**
   * Get the marker type to show on the map for the provided potential marker types and selected
   * filters.
   *
   * A particular location may have multiple location types so this function helps
   * prioritize what type of location it should appear as based on the selected filters.
   *
   * @param {Array<String>} types - The different types this marker can be.
   * @param {Array<String>} filters - The currently selected filters.
   * @return {Object} - Properties of the chosen marker type.
   */
  getLocationType(types, filters) {
    // If there are no types provided, then return null
    if (!types || types.length === 0) return null;

    // Make sure the marker type pertains to a selected filter in the case a marker has multiple
    // potential marker types.
    let type;

    for (let i = 0; i < types.length; i++) {
      const current = types[i];
      if (filters.indexOf(current) > -1) {
        type = current;
        break;
      }
    }
    
    if (!type || !markerIcon[type]) return null;
    return type;
  }

  /**
   * Provides Google Maps icons data for the provided marker type.
   *
   * @param {Object} location - Location data.
   * @param {String} locationType - Location type for this marker.
   * @param {Boolean} selected - Whether or not this location is selected.
   * @param {Number} zoom - Current zoom level.
   * @param {Object} google - Reference to Google object.
   * @return {Object} - Icon for marker.
   */
  getMarkerIcon(location, locationType, selected, zoom, google) {
    const markerType = markerIcon[locationType];

    let iconType = ICON_TYPES.default;
    let iconSize = markerType.size.marker;

    if (zoom < MAX_MARKER_ZOOM) {
      iconType = ICON_TYPES.dot;
      iconSize = markerType.size.dot;
    } else if (selected) {
      iconType = ICON_TYPES.selected;
      iconSize = markerType.size.selected;
    } else if (location.upcoming) {
      iconType = ICON_TYPES.upcoming;
    }

    return {
      url: markerType.types[iconType],
      scaledSize: new google.maps.Size(iconSize.height, iconSize.width),
    };
  }

  /**
   * Update marker icons based on the currently selected filters.
   *
   * This is necessary to do if a location has multiple types and location's current type (icon) has
   * been removed as a filter. We now need to find a new icon to show for this location.
   *
   * @param {Array<Object>} markers - Marker objects to update.
   * @param {Array<String>} filters - Currently selected filters.
   * @param {Number} zoom - Current zoom level.
   * @param {Object} selected - Selected marker.
   * @param {Object} google - Google reference.
   * @return {Promise} - Promise which resolves the updated markers.
   */
  updateMarkers(markers, filters, zoom, selected, google) {
    const updatedMarkers = { ...markers };

    Object.keys(updatedMarkers).forEach(key => {
      const { marker, location, icon } = updatedMarkers[key];
      const isSelected = selected && location.id === selected.id
      const locationType = this.getLocationType(location.types, filters) || DEFAULT;
      const markerIcon = this.getMarkerIcon(location, locationType, isSelected, zoom, google);

      if (markerIcon.url !== icon.url) {
        marker.setIcon(markerIcon);
      }

      updatedMarkers[key] = {
        marker,
        location,
        icon: markerIcon,
      };
    });

    return updatedMarkers;
  }

  createMarker(marker, map) {
    const markerType = (marker.types && marker.types.length > 0) ? marker.types[0] : DEFAULT;

    const customIcon = {
      url: markerIcon[markerType].types[markerType],
      scaledSize: new google.maps.Size(markerIcon[markerType].size.marker.height, markerIcon[markerType].size.marker.width),
    };

    return new google.maps.Marker({
      position: new google.maps.LatLng(marker.lat, marker.lng),
      icon: customIcon,
      map,
    });
  }

  /**
   * Creates map markers for the provided locations.
   *
   * @param {Array<Object>} markers - Marker objects to add to the map.
   * @param {Object} map - Google map reference.
   * @param {Array<String>} filters - Currently selected filters.
   * @param {Number} - Current zoom level.
   * @param {Array<String>} click - Click event listener.
   * @param {Object} selected - Currently selected location.
   * @param {Object} google - Google reference.
   * @return {Object} - Markers object that maps location id to marker reference and location data.
   */
  createMarkers(locations, map, filters, zoom, selected, click, google) {
    const markers = {};

    locations.forEach((location) => {
      // Prevent duplicates for the same marker id.
      if (markers[location.id]) return;

      const isSelected = selected && selected.id === location.id;
      const locationType = this.getLocationType(location.types, filters) || DEFAULT;
      const markerIcon = this.getMarkerIcon(location, locationType, isSelected, zoom, google);

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(location.lat, location.lng),
        icon: markerIcon,
        map,
        visible: false,
      });

      // Add click event listener.
      marker.addListener('click', click.bind(null, location, locationType));

      // Create the new entry with the Google maps marker reference and location data.
      markers[location.id] = {
        marker,
        location,
        icon: markerIcon,
      };

      markers[location.id].marker.setVisible(true);
    });

    return markers;
  }
}

export default new MarkerUtil();
